/**
 * Where an event stands against today, and its dates in the short form the
 * event cards use.
 *
 * An event's start and end are `yyyy-mm-dd` strings with no zone, so every
 * comparison here is on calendar days. A day is read as a UTC day number and
 * never as an instant: subtracting two local midnights is off by an hour
 * across a daylight saving change, and `Math.round` would only hide that for
 * some zones. "Today" is the reader's own calendar day, because an event
 * without a zone has no better clock to be measured against.
 */

const MONTHS = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];

const DAY_MS = 24 * 60 * 60 * 1000;

export type Day = { year: number; month: number; day: number };

/** A real `yyyy-mm-dd` calendar day, or null for anything else. */
export function parseDay(value: string | null | undefined): Day | null {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value ?? "");
  if (!match) return null;
  const [year, month, day] = [match[1], match[2], match[3]].map(Number);
  const check = new Date(Date.UTC(year, month - 1, day));
  // Date.UTC rolls 2026-02-30 over to March, so a round trip is the test.
  if (
    check.getUTCFullYear() !== year ||
    check.getUTCMonth() !== month - 1 ||
    check.getUTCDate() !== day
  ) {
    return null;
  }
  return { year, month, day };
}

function dayNumber({ year, month, day }: Day): number {
  return Date.UTC(year, month - 1, day) / DAY_MS;
}

/** The reader's calendar day as `yyyy-mm-dd`. */
export function todayLocal(now: Date): string {
  const month = String(now.getMonth() + 1).padStart(2, "0");
  const day = String(now.getDate()).padStart(2, "0");
  return `${now.getFullYear()}-${month}-${day}`;
}

export type EventPhase =
  | { kind: "upcoming"; days: number; singleDay?: boolean }
  | { kind: "live" }
  | { kind: "ended" }
  | { kind: "unknown" };

/**
 * `upcoming` with `days: 0` is the first day of the event. Without a time of
 * day there is no telling whether it has begun, so that day reads "Starts
 * today" and "live" begins with the second day. A one-day event reads "Today"
 * instead, which stays true after it has started.
 *
 * A missing end date is a one-day event. A missing or unreadable start date
 * is `unknown`: the event can be neither next nor over.
 */
export function eventPhase(
  startDate: string | null | undefined,
  endDate: string | null | undefined,
  today: string,
): EventPhase {
  const start = parseDay(startDate);
  const now = parseDay(today);
  if (!start || !now) return { kind: "unknown" };

  const first = dayNumber(start);
  const last = Math.max(first, dayNumber(parseDay(endDate) ?? start));
  const current = dayNumber(now);

  if (current < first) return { kind: "upcoming", days: first - current };
  if (current > last) return { kind: "ended" };
  if (current !== first) return { kind: "live" };
  return { kind: "upcoming", days: 0, singleDay: last === first };
}

/** The status pill on the hero card; null for a phase that has none. */
export function phaseLabel(phase: EventPhase): string | null {
  if (phase.kind === "live") return "Live now";
  if (phase.kind !== "upcoming") return null;
  if (phase.days === 0) return phase.singleDay ? "Today" : "Starts today";
  if (phase.days === 1) return "Starts tomorrow";
  return `Starts in ${phase.days} days`;
}

/**
 * "21–22 Oct", "29 Sep – 2 Oct", "3 Dec". The year appears only
 * when the date is not in the reader's current year, so a card from last
 * autumn cannot be mistaken for this one.
 *
 * Text that is not a date is returned as it is, so a bad value shows instead
 * of vanishing.
 */
export function formatDateRange(
  startDate: string | null | undefined,
  endDate: string | null | undefined,
  today: string,
): string {
  if (!startDate && !endDate) return "";
  const start = parseDay(startDate ?? endDate);
  if (!start) return startDate ?? endDate ?? "";
  const parsedEnd = parseDay(endDate);
  const end =
    parsedEnd && dayNumber(parsedEnd) > dayNumber(start) ? parsedEnd : null;
  const thisYear = parseDay(today)?.year;
  // A range over New Year names both years, whatever year it is now.
  const spansYears = end !== null && end.year !== start.year;
  const withYear = (day: Day) =>
    spansYears || day.year !== thisYear ? ` ${day.year}` : "";

  if (!end) return `${start.day} ${MONTHS[start.month - 1]}${withYear(start)}`;

  if (start.year === end.year && start.month === end.month) {
    return `${start.day}–${end.day} ${MONTHS[end.month - 1]}${withYear(end)}`;
  }
  return (
    `${start.day} ${MONTHS[start.month - 1]}${withYear(start)}` +
    ` – ${end.day} ${MONTHS[end.month - 1]}${withYear(end)}`
  );
}
